import { Controller } from "@hotwired/stimulus"

const STORAGE_PREFIX = "lumenfall:onboarding:setup_complete:"

/**
 * Two-state "I'm done setting up" footer. Trigger and waiting message
 * sit in the same grid cell so the container's height never changes,
 * and we cross-fade with opacity only. State persists in localStorage
 * keyed by the value attribute (e.g. "developer", "external_gui"); on
 * connect we jump straight to the waiting state if previously set.
 *
 *   <div data-controller="setup-complete"
 *        data-setup-complete-key-value="developer">
 *     <div class="grid">
 *       <div data-setup-complete-target="trigger"
 *            class="col-start-1 row-start-1 transition-opacity duration-200">…</div>
 *       <div data-setup-complete-target="waiting"
 *            class="col-start-1 row-start-1 transition-opacity duration-200
 *                   opacity-0 pointer-events-none">…</div>
 *     </div>
 *   </div>
 */
export default class SetupCompleteController extends Controller {
  static targets = ["trigger", "waiting"]
  static values = { key: String }

  declare readonly triggerTarget: HTMLElement
  declare readonly waitingTarget: HTMLElement
  declare readonly keyValue: string

  connect() {
    if (!this.isCompleted()) return

    this.triggerTarget.classList.remove("transition-opacity", "duration-200")
    this.waitingTarget.classList.remove("transition-opacity", "duration-200")
    this.triggerTarget.classList.add("opacity-0", "pointer-events-none")
    this.waitingTarget.classList.remove("opacity-0", "pointer-events-none")

    requestAnimationFrame(() => {
      this.triggerTarget.classList.add("transition-opacity", "duration-200")
      this.waitingTarget.classList.add("transition-opacity", "duration-200")
    })
  }

  reveal(event: Event) {
    event.preventDefault()
    this.triggerTarget.classList.add("opacity-0", "pointer-events-none")
    this.waitingTarget.classList.remove("opacity-0", "pointer-events-none")
    this.markCompleted()
  }

  private storageKey(): string {
    return `${STORAGE_PREFIX}${this.keyValue}`
  }

  private isCompleted(): boolean {
    try {
      return localStorage.getItem(this.storageKey()) === "1"
    } catch {
      return false
    }
  }

  private markCompleted() {
    try {
      localStorage.setItem(this.storageKey(), "1")
    } catch {
      // localStorage unavailable (private mode etc); state will be
      // ephemeral but the UI still works for this session.
    }
  }
}
